import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Returns the uploaded image model that should represent a selected device.
 * Plus and mini use the standard model image; Pro Max uses the Pro image.
 * iPhone 17 Air keeps its own dedicated image.
 *
 * @param {string | undefined} device
 * @returns {{ number: string, type: 'base' | 'pro' | 'air' } | null}
 */
function getDeviceMediaTarget(device) {
  if (!device) return null;

  const match = device.trim().match(/^iPhone\s+(\d+)(?:\s+(Pro Max|Pro|Plus|mini|Air))?$/i);
  if (!match) return null;

  const [, number, model = ''] = match;
  let type = 'base';

  if (number === '17' && /^air$/i.test(model)) {
    type = 'air';
  } else if (/^pro(?:\s+max)?$/i.test(model)) {
    type = 'pro';
  }

  return { number, type };
}

/**
 * @param {HTMLImageElement} image
 * @param {{ number: string, type: 'base' | 'pro' | 'air' }} target
 */
function imageMatchesDevice(image, target) {
  let source = image.currentSrc || image.src || '';

  try {
    source = decodeURIComponent(source);
  } catch {
    // Keep the original source when a CDN URL contains an invalid escape sequence.
  }

  const text = (image.alt + ' ' + source)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const prefix = 'iphone(?: case)? ' + target.number;

  if (target.type === 'pro') {
    return new RegExp('\\b' + prefix + ' pro(?! max)\\b').test(text);
  }

  if (target.type === 'air') {
    return new RegExp('\\b' + prefix + ' air\\b').test(text);
  }

  return new RegExp('\\b' + prefix + '(?! (?:pro|max|plus|mini|air))\\b').test(text);
}

/**
 * @param {Document | Element} source
 * @returns {string | undefined}
 */
function getSelectedDevice(source) {
  const selectedOptions = source.querySelectorAll(
    'variant-picker select option:checked, variant-picker select option[selected], variant-picker fieldset input:checked'
  );

  for (const option of selectedOptions) {
    const value =
      option instanceof HTMLInputElement || option instanceof HTMLOptionElement
        ? option.value || option.textContent || ''
        : option.textContent || '';

    if (/^iPhone\s+\d+/i.test(value.trim())) return value.trim();
  }

  return undefined;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#scheduleMappedDeviceMedia(getSelectedDevice(document));
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const device = event.detail.resource?.options?.find((option) => /^iPhone\s+\d+/i.test(option)) ??
      getSelectedDevice(source);

    this.replaceWith(newMediaGallery);
    newMediaGallery.#scheduleMappedDeviceMedia(device);
  };

  /**
   * Runs after the slideshow finishes its own connected initialization.
   *
   * @param {string | undefined} device
   */
  #scheduleMappedDeviceMedia(device) {
    requestAnimationFrame(() => {
      setTimeout(() => this.#selectMappedDeviceMedia(device), 100);
    });
  }

  /**
   * Selects the image uploaded for the standard or Pro model represented by the chosen device.
   *
   * @param {string | undefined} device
   */
  #selectMappedDeviceMedia(device) {
    const target = getDeviceMediaTarget(device);
    if (!target) return;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const slideIndex = slides.findIndex((slide) => {
      const image = slide.querySelector('img');
      return image instanceof HTMLImageElement && imageMatchesDevice(image, target);
    });

    if (slideIndex >= 0) {
      this.slideshow?.select(slideIndex, undefined, { animate: false });
    }

    const gridItems = Array.from(this.querySelectorAll('.media-gallery__grid > [ref="media[]"]'));
    const gridItem = gridItems.find((item) => {
      const image = item.querySelector('img');
      return image instanceof HTMLImageElement && imageMatchesDevice(image, target);
    });

    if (gridItem instanceof HTMLElement) {
      gridItem.style.order = '-1';
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
